import { allows } from "@giftforward/entitlements";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import type { LandingSettings, PlanUpsellContext } from "@/context/CampaignBuilderContext";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { CompanyLogoSection } from "../CompanyLogoSection";
import { HeroBannerSection } from "../HeroBannerSection";
import { PillToggle } from "../PillToggle";
import ConfettiControls from "./ConfettiControls";
import CustomColorsControls from "./CustomColorsControls";
import ProFeaturesAccordion from "./ProFeaturesAccordion";

interface LandingBrandingPanelProps {
  state: LandingSettings;
  onChange: (v: Partial<LandingSettings>) => void;
  /** Fire a sample confetti burst (scheme index, or null for the brand palette). */
  onPreviewConfetti: (schemeIndex: number | null) => void;
}

/**
 * Landing → Branding tab. Reuses the email drawer's CompanyLogoSection +
 * HeroBannerSection (wired to the landing slice, cropping the landing images), then
 * the two plan-gated Pro accordions: Custom Colors and Confetti.
 */
export default function LandingBrandingPanel({
  state,
  onChange,
  onPreviewConfetti,
}: LandingBrandingPanelProps) {
  const { openCropDialog, campaignId, openModal, openPlanModal, stage3Email } =
    useCampaignBuilder();
  const { ent } = useMyEntitlements();
  const canUseColors = !!ent && allows(ent, "landing", "customize", "colors");
  const canUseConfetti = !!ent && allows(ent, "landing", "customize", "confetti");
  const emailLogoUrl = stage3Email.logoUrl;

  // Open a feature's "Unlock …" upgrade modal; its primary CTA opens the plan upgrade
  // modal on top of the builder, which stays mounted underneath with state intact.
  const openUpgrade = (context: PlanUpsellContext) =>
    openModal({ context, onPrimary: () => openPlanModal(context) });

  return (
    <div className="px-5">
      {/* Landing logo: synced from the email logo by default, or unlinked to its own image. */}
      {state.logoSynced ? (
        <div className="py-4">
          <div className="flex items-center justify-between mb-3">
            <span
              className="font-lato text-[13px] font-bold text-rodeo-black uppercase"
              style={{ letterSpacing: "0.5px" }}
            >
              Company Logo
            </span>
            <div className="flex items-center gap-2">
              <span className="font-lato text-[11px] font-semibold uppercase tracking-[0.5px] text-graphite">
                Show
              </span>
              <PillToggle
                checked={state.logoEnabled}
                onChange={(v) => onChange({ logoEnabled: v })}
                aria-label="Toggle landing logo"
              />
            </div>
          </div>
          {state.logoEnabled && (
            <div
              className="flex items-center gap-3"
              style={{
                border: "1px solid var(--color-smoke)",
                borderRadius: 8,
                padding: "10px 12px",
                background: "var(--color-wax)",
              }}
            >
              {emailLogoUrl ? (
                <img
                  // Plain display <img> — no CORS needed to show an image. Only CropDialog reads
                  // pixels (canvas) and it does so through its own crop-only cache key.
                  src={emailLogoUrl}
                  alt="Email logo"
                  className="object-contain"
                  style={{
                    maxWidth: 72,
                    maxHeight: 32,
                    borderRadius: 4,
                    border: "1px solid var(--color-smoke)",
                    padding: 4,
                    background: "#fff",
                    boxSizing: "border-box",
                  }}
                />
              ) : null}
              <div className="flex-1">
                <p className="font-lato text-[12px] font-semibold text-rodeo-black">
                  Synced from Email
                </p>
                <p className="font-lato text-[11px] text-graphite">
                  {emailLogoUrl
                    ? "Uses your email logo automatically."
                    : "Add a logo in the Email tab, or unlink to upload one here."}
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  onChange({
                    logoSynced: false,
                    logoUrl: state.logoUrl || emailLogoUrl,
                    // Seed the crop source from the email's original (or its displayed logo) so the
                    // unlinked landing logo can be re-cropped from the full image.
                    logoOriginalUrl:
                      state.logoOriginalUrl || stage3Email.logoOriginalUrl || emailLogoUrl,
                  })
                }
                className="font-lato text-[11px] font-bold uppercase tracking-[0.5px] whitespace-nowrap"
                style={{
                  border: "1px solid var(--color-good-night)",
                  color: "var(--color-good-night)",
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#fff",
                  cursor: "pointer",
                }}
                title="Break the sync and edit this logo independently on the landing page"
              >
                Unlink &amp; Edit
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <CompanyLogoSection
            enabled={state.logoEnabled}
            logoUrl={state.logoUrl}
            logoPadding={state.logoPadding}
            campaignId={campaignId}
            mediaType="landing-logo"
            onChange={onChange}
            onCrop={() => openCropDialog("landing-logo")}
          />
          <button
            type="button"
            onClick={() => onChange({ logoSynced: true })}
            className="font-lato text-[11px] font-semibold underline pb-3"
            style={{
              color: "var(--color-good-night)",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            ↺ Re-sync logo from Email
          </button>
        </>
      )}

      <div style={{ borderTop: "1px solid var(--color-card-border)" }}>
        {/* The landing banner is not croppable (prototype parity) — no onCrop. */}
        <HeroBannerSection
          enabled={state.bannerEnabled}
          bannerUrl={state.bannerUrl}
          campaignId={campaignId}
          mediaType="landing-banner"
          onChange={onChange}
          bannerHint="Recommended: 600 × 400px — PNG or JPG"
        />
      </div>

      <div className="py-4" style={{ borderTop: "1px solid var(--color-card-border)" }}>
        <ProFeaturesAccordion
          icon={<PaletteOutlinedIcon sx={{ fontSize: 18 }} />}
          title="Custom Colors"
          locked={!canUseColors}
          unlockLabel="Unlock Custom Colors"
          onUpgrade={() => openUpgrade("landingBrandColors")}
        >
          <CustomColorsControls settings={state} onChange={onChange} />
        </ProFeaturesAccordion>

        <ProFeaturesAccordion
          icon={<CelebrationOutlinedIcon sx={{ fontSize: 18 }} />}
          title="Confetti"
          locked={!canUseConfetti}
          unlockLabel="Unlock Confetti"
          onUpgrade={() => openUpgrade("landingConfetti")}
        >
          <ConfettiControls settings={state} onChange={onChange} onPreview={onPreviewConfetti} />
        </ProFeaturesAccordion>
      </div>
    </div>
  );
}
